import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Loader2, Eye, EyeOff, Smartphone, ArrowRight, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PhoneVerificationModal from '../../components/auth/PhoneVerificationModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function b64encode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function b64decode(str) {
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) str += '=';
  const bin = atob(str);
  return Uint8Array.from(bin, c => c.charCodeAt(0)).buffer;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshUser } = useAuth();
  
  // Get redirect URL from query parameter or location state
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const from = redirectParam || location.state?.from || '/parent';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Inline validation state for the email/password form.
  const [errors, setErrors] = useState({});
  const clearError = (field) => {
    setErrors((prev) => {
      if (!prev[field] && !prev.general) return prev;
      const next = { ...prev };
      delete next[field];
      delete next.general;
      return next;
    });
  };

  // Phone login state
  const [loginMode, setLoginMode] = useState('email'); // 'email' | 'phone'
  const [phone, setPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneStep, setPhoneStep] = useState('enter'); // 'enter' | 'verify'
  const [sendingCode, setSendingCode] = useState(false);

  // Passkey / Biometric state
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  // Post-login phone verification prompt
  const [pendingVerifyUser, setPendingVerifyUser] = useState(null);
  const [pendingRedirect, setPendingRedirect] = useState(null);

  useEffect(() => {
    // Check if passkeys are available and browser supports WebAuthn
    if (window.PublicKeyCredential) {
      axios.get(`${API_URL}/api/auth/passkey/available`)
        .then(res => setPasskeyAvailable(res.data.available))
        .catch(() => {});
    }
  }, []);

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      // Get authentication options from server
      const optionsRes = await axios.post(`${API_URL}/api/auth/passkey/authenticate/options`);
      const options = optionsRes.data;

      // Call WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: b64decode(options.challenge),
          timeout: options.timeout,
          rpId: options.rpId,
          allowCredentials: options.allowCredentials.map(c => ({
            type: c.type,
            id: b64decode(c.id),
          })),
          userVerification: options.userVerification,
        },
      });

      // Send assertion to server
      const verifyRes = await axios.post(`${API_URL}/api/auth/passkey/authenticate/verify`, {
        id: b64encode(credential.rawId),
        challenge_key: options.challenge_key,
        response: {
          authenticatorData: b64encode(credential.response.authenticatorData),
          clientDataJSON: b64encode(credential.response.clientDataJSON),
          signature: b64encode(credential.response.signature),
        },
      });

      const data = verifyRes.data;
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('refreshToken', data.refresh_token);
      toast.success(`Welcome back, ${data.user.first_name}!`);

      const role = data.user.role;
      // Use redirect URL if available
      if (redirectParam) {
        window.location.href = redirectParam;
      } else if (role === 'admin' || role === 'super_admin') {
        window.location.href = '/admin';
      } else if (role === 'instructor') {
        window.location.href = '/instructor';
      } else if (role === 'partner') {
        window.location.href = '/partner';
      } else {
        window.location.href = '/parent';
      }
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error('Biometric authentication was cancelled');
      } else {
        toast.error(error.response?.data?.error || 'Passkey authentication failed');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handlePostLoginRedirect = async () => {
    // Refresh the cached user so phone_verified / phone_verification_skipped
    // reflect the latest server state before we leave the page.
    if (refreshUser) {
      try { await refreshUser(); } catch (_) {}
    }
    setPendingVerifyUser(null);
    const target = pendingRedirect || '/parent';
    setPendingRedirect(null);
    window.location.href = target;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Inline validation — replaces the toast-only "please fill in all fields".
    const errs = {};
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = 'Please enter a valid email address';
    if (!password) errs.password = 'Password is required';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.first_name}!`);
      
      // Navigate based on role - use window.location for reliable redirect
      let redirectPath;
      
      // If there's a redirect parameter (e.g., from registration page), use that
      if (redirectParam) {
        redirectPath = redirectParam;
      } else if (user.role === 'admin' || user.role === 'super_admin') {
        redirectPath = '/admin';
      } else if (user.role === 'instructor') {
        redirectPath = '/instructor';
      } else if (user.role === 'partner') {
        redirectPath = '/partner';
      } else if (user.role === 'volunteer') {
        redirectPath = '/instructor';
      } else {
        // For parents, use the stored 'from' path or default to /parent
        const hasCampContext = localStorage.getItem('pendingCampRegistration');
        const target = typeof from === 'string' ? from : (from?.pathname || '/parent');
        redirectPath = hasCampContext ? '/parent?from=camp' : target;
      }
      
      // If the user has a phone number but hasn't verified it and hasn't
      // explicitly skipped, prompt them to verify now (gating the redirect).
      const needsPhoneVerify = !!user.phone
        && user.phone_verified !== true
        && user.phone_verification_skipped !== true;
      if (needsPhoneVerify) {
        setPendingVerifyUser(user);
        setPendingRedirect(redirectPath);
        setLoading(false);
        return;
      }

      // Use window.location for full page reload to ensure auth state is fresh
      window.location.href = redirectPath;
    } catch (error) {
      const data = error.response?.data;
      let message = 'Login failed. Please try again.';
      if (data) {
        if (data.detail) message = data.detail;
        else if (data.non_field_errors) message = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        else if (typeof data === 'object') {
          const first = Object.values(data)[0];
          message = Array.isArray(first) ? first[0] : String(first);
        }
      }
      // Show invalid-credentials style errors inline on both fields rather
      // than as a fleeting toast, so the user can see what to correct.
      const status = error.response?.status;
      if (status === 400 || status === 401 || status === 403) {
        setErrors({ general: message, email: ' ', password: ' ' });
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneCode = async () => {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setSendingCode(true);
    try {
      await axios.post(`${API_URL}/api/auth/phone/send-code/`, { phone, purpose: 'login' });
      toast.success(`Code sent to ***${phone.slice(-4)}`);
      setPhoneStep('verify');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyPhoneLogin = async () => {
    if (phoneCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/phone/verify-code/`, { phone, code: phoneCode, purpose: 'login' });
      if (res.data.access_token) {
        localStorage.setItem('token', res.data.access_token);
        localStorage.setItem('refreshToken', res.data.refresh_token);
        toast.success('Welcome back!');
        
        // Use redirect URL if available, otherwise route by role
        if (redirectParam) {
          window.location.href = redirectParam;
        } else {
          const role = res.data.user?.role;
          const target = role === 'admin' ? '/admin' : role === 'instructor' ? '/instructor' : '/parent';
          window.location.href = target;
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md card-marketing">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center justify-center mb-4">
            <img 
              src="/brand/rss-logo-horizontal.png" 
              alt="Ready Set STEM" 
              className="h-20 w-auto"
            />
          </Link>
          <CardTitle className="text-2xl font-heading text-rsc-navy">Welcome Back</CardTitle>
          <CardDescription>Sign in to your account to continue</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Biometric / Passkey Login */}
          {passkeyAvailable && window.PublicKeyCredential && (
            <div className="mb-4">
              <Button
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading}
                className="w-full h-12 bg-gradient-to-r from-rsc-navy to-[#2a4a7f] hover:from-[#1a3a6d] hover:to-[#3a5a8f] text-white font-bold rounded-xl shadow-lg"
                data-testid="passkey-login-btn"
              >
                {passkeyLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Fingerprint className="h-5 w-5 mr-2" />
                )}
                {passkeyLoading ? 'Verifying...' : 'Sign in with Face ID / Fingerprint'}
              </Button>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400 font-medium">or use email / phone</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            </div>
          )}

          {/* Login Mode Toggle */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-4">
            <button onClick={() => setLoginMode('email')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${loginMode === 'email' ? 'bg-white shadow text-rsc-navy' : 'text-gray-500'}`}>
              Email & Password
            </button>
            <button onClick={() => setLoginMode('phone')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1.5 ${loginMode === 'phone' ? 'bg-white shadow text-rsc-navy' : 'text-gray-500'}`}>
              <Smartphone className="w-3.5 h-3.5" /> Phone Code
            </button>
          </div>

          {/* Email/Password Login */}
          {loginMode === 'email' && (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            {errors.general && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200" data-testid="login-error-general">
                <p className="text-sm text-red-600 font-medium">{errors.general}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="parent@example.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); clearError('email'); }}
                disabled={loading}
                className={errors.email && errors.email.trim() ? 'border-red-500 focus-visible:ring-red-500' : errors.email ? 'border-red-500' : ''}
                data-testid="email-input"
              />
              {errors.email && errors.email.trim() && (
                <p className="text-xs text-red-500 mt-1" data-testid="login-error-email">{errors.email}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm text-rsc-blue hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError('password'); }}
                  disabled={loading}
                  className={errors.password && errors.password.trim() ? 'border-red-500 focus-visible:ring-red-500' : errors.password ? 'border-red-500' : ''}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && errors.password.trim() && (
                <p className="text-xs text-red-500 mt-1" data-testid="login-error-password">{errors.password}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Sign In
            </Button>

            {/* Alternative: sign in with an SMS code */}
            <button
              type="button"
              onClick={() => setLoginMode('phone')}
              className="w-full text-center text-sm text-gray-500 hover:text-rsc-navy transition-colors flex items-center justify-center gap-1.5 pt-1"
              data-testid="switch-to-phone-login-btn"
            >
              <Smartphone className="w-3.5 h-3.5" />
              Forgot password? Sign in with a phone code instead
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>
          )}

          {/* Phone Login */}
          {loginMode === 'phone' && (
            <div className="space-y-4">
              {phoneStep === 'enter' ? (
                <>
                  <div className="space-y-2">
                    <Label>Mobile Phone Number</Label>
                    <Input type="tel" placeholder="(555) 123-4567" value={phone}
                      onChange={e => setPhone(e.target.value)} data-testid="phone-login-input" />
                    <p className="text-xs text-muted-foreground">We'll send a 6-digit verification code to this number</p>
                  </div>
                  <Button onClick={handleSendPhoneCode} className="w-full" disabled={sendingCode}>
                    {sendingCode ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Smartphone className="h-4 w-4 mr-2" />}
                    {sendingCode ? 'Sending Code...' : 'Send Verification Code'}
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-center mb-2">
                    <Smartphone className="w-8 h-8 text-rsc-navy mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Enter the code sent to <strong>***{phone.slice(-4)}</strong></p>
                  </div>
                  <Input type="text" maxLength={6} value={phoneCode}
                    onChange={e => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-[0.5em] font-mono h-14"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    data-testid="phone-code-input" autoFocus />
                  <Button onClick={handleVerifyPhoneLogin} className="w-full" disabled={loading || phoneCode.length !== 6}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                    {loading ? 'Verifying...' : 'Sign In'}
                  </Button>
                  <div className="flex justify-between">
                    <button onClick={() => setPhoneStep('enter')} className="text-xs text-gray-500 hover:underline">Change number</button>
                    <button onClick={handleSendPhoneCode} className="text-xs text-blue-600 hover:underline" disabled={sendingCode}>Resend code</button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/auth/register" className="text-primary font-medium hover:underline" data-testid="register-link">
              Sign up
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Post-login phone verification prompt for existing unverified users */}
      <PhoneVerificationModal
        open={!!pendingVerifyUser}
        phone={pendingVerifyUser?.phone}
        purpose="registration"
        allowSkip={true}
        authToken={localStorage.getItem('token')}
        title="Please verify your phone number"
        description="Takes 10 seconds — we'll text you a 6-digit code. You can skip, but we'll ask again next time."
        onVerified={handlePostLoginRedirect}
        onSkip={handlePostLoginRedirect}
        onClose={handlePostLoginRedirect}
      />
    </div>
  );
}
